<script setup lang="ts" generic="TMeta = unknown">
  import { computed, inject, type HTMLAttributes } from 'vue'
  import { IconCheck, IconChevronDown } from '@gat-ship/icons'
  import {
    Combobox,
    ComboboxAnchor,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxTrigger
  } from '../combobox'
  import { FIELD_CONTEXT_KEY } from '../field'
  import { cn } from '../../../lib/utils'
  import InputGroupAddon from './InputGroupAddon.vue'
  import { filterSelectInputOptions } from '../select-input'
  import type { SelectInputItem } from '../select-input'

  /**
   * A type-to-filter combobox control sized to slot into a `FormField` /
   * `InputGroup` exactly like `InputGroupInput`. It brings NO border of its
   * own — the surrounding `InputGroup` owns the chrome, and the control
   * participates in the parent's focus / error ring via the
   * `data-slot="input-group-control"` + `aria-invalid` hooks on the inner
   * input.
   *
   * This is the borderless, input-group-native sibling of `ComboboxSelect`
   * (which is a standalone, self-bordered control). Decoupled from any data
   * source — pass `items` in.
   *
   * Slots:
   * - `leading` (`{ item }`): rendered before the input, e.g. an
   *   `InputGroupAddon` with an icon/flag for the selected item.
   * - `content-header`: a sticky-top region inside the dropdown, above the
   *   rows — e.g. a title + "Manage" affordance (see `AgencyPicker`).
   * - `empty`: the dropdown body shown when no items match — override the
   *   default "No results" text with a domain-specific message.
   * - `item` (`{ item, selected }`): a single dropdown row.
   *
   * Usage (inside a FormField, like any other input-group control):
   * ```vue
   * <FormField :form="form" name="x" label="X" required>
   *   <template #default="{ field, invalid }">
   *     <InputGroupCombobox
   *       :id="field.name"
   *       :items="items"
   *       :model-value="field.state.value"
   *       :aria-invalid="invalid"
   *       @update:model-value="field.handleChange"
   *       @blur="field.handleBlur"
   *     />
   *   </template>
   * </FormField>
   * ```
   */
  const props = defineProps<{
    items: SelectInputItem<TMeta>[]
    modelValue?: string
    id?: string
    placeholder?: string
    dataTestid?: string
    disabled?: boolean
    /** Async-loading the items list itself (not the value). */
    loading?: boolean
    ariaInvalid?: boolean
    /** Class forwarded to the inner combobox input. */
    inputClass?: HTMLAttributes['class']
  }>()

  const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void
    (e: 'blur'): void
  }>()

  const slots = defineSlots<{
    leading?: (props: { item: SelectInputItem<TMeta> | undefined }) => unknown
    'content-header'?: () => unknown
    empty?: () => unknown
    item?: (props: {
      item: SelectInputItem<TMeta>
      selected: boolean
    }) => unknown
  }>()

  function findItem(
    value: string | undefined
  ): SelectInputItem<TMeta> | undefined {
    if (!value) return undefined
    return props.items.find((i) => i.value === value)
  }

  const selectedItem = computed(() => findItem(props.modelValue))

  function displayValue(val: unknown): string {
    const code = String(val ?? '')
    if (!code) return ''
    return findItem(code)?.title ?? code
  }

  function filterItems(options: string[], term: string): string[] {
    return filterSelectInputOptions(props.items, options, term)
  }

  // The whole app runs under a stacked `FieldRoot`, where a `FieldGroup`
  // strips the height/padding of sibling inputs via reset classes keyed on
  // `data-slot=input | combobox-input`. Our input carries
  // `data-slot=input-group-control` (so the parent InputGroup focus / error
  // ring picks it up), which escapes those resets — so we mirror the stacked
  // height treatment here to stay flush with neighbouring text fields.
  const fieldContext = inject(FIELD_CONTEXT_KEY, {
    variant: 'default' as const
  })
  const stacked = computed(() => fieldContext.variant === 'stacked')

  // Horizontal alignment with the field label (which sits at `px-3`): a
  // selected item's leading addon already carries `pl-3`, so the input only
  // needs its own `pl-3` for the empty / placeholder state. With a leading
  // addon present the input hugs it (`pl-1`).
  const hasLeading = computed(() => !!slots.leading && !!selectedItem.value)
</script>

<template>
  <Combobox
    class="w-full"
    :model-value="modelValue ?? ''"
    :filter-function="filterItems"
    :disabled="disabled"
    :open-on-focus="true"
    @update:model-value="(v) => emit('update:modelValue', String(v ?? ''))"
    @update:open="
      (open) => {
        if (!open) emit('blur')
      }
    "
  >
    <ComboboxAnchor class="flex w-full items-center gap-1.5">
      <slot name="leading" :item="selectedItem" />
      <ComboboxInput
        :id="id"
        data-slot="input-group-control"
        :aria-invalid="ariaInvalid || undefined"
        :display-value="displayValue"
        :placeholder="loading ? 'Loading…' : placeholder"
        :disabled="disabled"
        :data-testid="dataTestid"
        :class="
          cn(
            'min-w-0 flex-1 bg-transparent px-0',
            stacked ? 'h-auto py-0' : 'h-9',
            hasLeading ? 'pl-1' : 'pl-3',
            inputClass
          )
        "
      />
      <InputGroupAddon align="inline-end" class="shrink-0 has-[>button]:mr-0">
        <ComboboxTrigger>
          <IconChevronDown class="text-muted-foreground size-4 opacity-50" />
        </ComboboxTrigger>
      </InputGroupAddon>
    </ComboboxAnchor>

    <ComboboxContent>
      <slot name="content-header" />
      <ComboboxEmpty>
        <slot name="empty">{{ loading ? 'Loading…' : 'No results' }}</slot>
      </ComboboxEmpty>
      <ComboboxItem
        v-for="item in items"
        :key="item.value"
        :value="item.value"
        :disabled="item.disabled"
        :data-testid="`combobox-item-${item.value}`"
      >
        <slot name="item" :item="item" :selected="modelValue === item.value">
          <span class="flex-1 truncate text-sm">{{ item.title }}</span>
          <IconCheck
            :class="[
              'ml-auto size-3.5 shrink-0',
              modelValue === item.value ? 'opacity-100' : 'opacity-0'
            ]"
          />
        </slot>
      </ComboboxItem>
    </ComboboxContent>
  </Combobox>
</template>
